import React, { useMemo } from 'react'
import InitialAvatar from 'shared/components/InitialAvatar/InitialAvatar'
import SelectDropdown from 'shared/ui/SelectDropdown/SelectDropdown'
import { useAddStudentSearch } from './hooks/useAddStudentSearch'
import s from './styles/AddStudentModal.module.scss'

const StudentCard = ({ student, className }) => (
	<>
		<InitialAvatar
			name={student.name}
			size={40}
			photoUrl={student.photoUrl}
			avatarKey={student.avatarKey}
			avatarSource={student.avatarSource}
		/>
		<span className={s.resultBody}>
			<span className={s.resultTop}>
				<strong className={s.resultName}>{student.name}</strong>
				{student.grade ? <span className={s.resultGrade}>{student.grade}</span> : null}
			</span>
			<span className={s.resultEmail}>{student.email}</span>
		</span>
	</>
)

export const AddStudentFields = ({
	counselors,
	counselorId,
	onCounselorChange,
	disabled = false,
	search,
}) => {
	const {
		nameQuery,
		setNameQuery,
		emailQuery,
		setEmailQuery,
		lookupResult,
		selectedStudent,
		selectStudent,
		clearSelection,
		lookupStatus,
		canSubmit,
	} = search

	const counselorOptions = useMemo(
		() => [
			{ value: '', label: 'Unassigned' },
			...(counselors || []).map(counselor => ({
				value: counselor.id,
				label: counselor.name,
			})),
		],
		[counselors],
	)

	return (
		<>
			<label className={s.searchField}>
				<span>Student name</span>
				<input
					type="text"
					value={nameQuery}
					onChange={event => setNameQuery(event.target.value)}
					placeholder="Student's full name"
					disabled={disabled}
					autoComplete="name"
				/>
			</label>
			<label className={s.searchField}>
				<span>Student email</span>
				<input
					autoFocus
					type="email"
					value={emailQuery}
					onChange={event => setEmailQuery(event.target.value)}
					placeholder="student@email.com"
					disabled={disabled}
					autoComplete="off"
				/>
			</label>

			{lookupStatus === 'loading' ? (
				<div className={`${s.status} ${s.statusLoading}`.trim()} role="status">
					Searching for an existing student account…
				</div>
			) : null}

			{lookupStatus === 'not_found' ? (
				<div className={`${s.status} ${s.statusNotFound}`.trim()} role="alert">
					<p className={s.statusTitle}>Student not found</p>
					<p className={s.statusText}>
						No account was found with this email. The student needs to register and log in
						first before they can be added to this school.
					</p>
				</div>
			) : null}

			{lookupStatus === 'counsellor_email' ? (
				<div className={`${s.status} ${s.statusNotFound}`.trim()} role="alert">
					<p className={s.statusTitle}>Student not found</p>
					<p className={s.statusText}>
						This email belongs to a counsellor account. Search for the student&apos;s own
						email address instead.
					</p>
				</div>
			) : null}

			{lookupStatus === 'found' && lookupResult ? (
				<button
					type="button"
					className={s.resultCard}
					onClick={() => selectStudent(lookupResult)}
					disabled={disabled}
				>
					<StudentCard student={lookupResult} />
				</button>
			) : null}

			{lookupStatus === 'already_linked' && lookupResult ? (
				<>
					<div className={s.selectedCard}>
						<StudentCard student={lookupResult} />
					</div>
					<div className={`${s.status} ${s.statusAlreadyLinked}`.trim()} role="alert">
						<p className={s.statusTitle}>Student already added</p>
						<p className={s.statusText}>This student is already part of this school.</p>
					</div>
				</>
			) : null}

			{lookupStatus === 'selected' && selectedStudent ? (
				<>
					<div className={s.selectedCard}>
						<StudentCard student={selectedStudent} />
					</div>
					<button type="button" className={s.changeLink} onClick={clearSelection} disabled={disabled}>
						Search for a different student
					</button>
				</>
			) : null}

			<div className={s.counselorField}>
				<span className={s.counselorLabel}>Counsellor (optional)</span>
				<SelectDropdown
					value={counselorId}
					onChange={onCounselorChange}
					options={counselorOptions}
					disabled={disabled || !canSubmit}
					placeholder="Unassigned"
					menuZIndex={3000}
				/>
			</div>
		</>
	)
}

export { useAddStudentSearch }
